import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import CellTowerIcon from '@mui/icons-material/CellTower';
import { dateHeader, formatCellDate } from './timeModeCell';
import { linkTo } from '../linkTo';
import { ColumnExtendedStrict } from '../../types/ColumnExtended';
import { VisitLogEntry } from '../../types/VisitLogEntry';
import { LocationMehod } from '../../constants/Constants';

const renderLocationMethod = (locationMethod) => {
  return locationMethod === LocationMehod.GPS ? (
    <SatelliteAltOutlinedIcon style={{ color: 'green', fontSize: '1.1rem' }} />
  ) : locationMethod === LocationMehod.NETWORK ? (
    <CellTowerIcon style={{ color: 'blue', fontSize: '1.1rem' }} />
  ) : (
    <></>
  );
};

// getTotalRows is read lazily at render time, so the "#" column always uses the latest server totalCount
export const makeVisitSightingColumns = (getTotalRows: () => number): ColumnExtendedStrict<VisitLogEntry>[] => [
  {
    header: '#',
    id: 'index',
    size: 50,
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;

      const absoluteIndex = pageIndex * pageSize + row.index;
      const total = getTotalRows();

      // Newest record gets the highest number; continues correctly across pages
      return <span style={{ color: '#7c3aed', fontWeight: 600 }}>{Math.max(total - absoluteIndex, 1)}</span>;
    },
  },
  {
    header: 'Reader',
    accessorKey: 'readerName',
    enableColumnFilter: true,
    size: 200,
    cell: ({ row }) => <span title={row.original.readerName}>{row.original.readerName ?? ' '}</span>,
  },
  {
    header: 'IMEI',
    accessorKey: 'imei',
    enableColumnFilter: true,
    size: 180,
    cell: ({ row }) => (row.original.imei ? linkTo('/readers', row.original.imei, row.original.imei) : '—'),
  },
  {
    header: 'Facility',
    accessorKey: 'facilityName',
    enableColumnFilter: true,
    size: 180,
    cell: ({ row }) => <span title={row.original.facilityName}>{row.original.facilityName ?? '—'}</span>,
  },
  {
    header: dateHeader('First Seen'),
    accessorKey: 'firstSeenDateTime',
    size: 150,
    cell: ({ row, table }) => (row.original.firstSeenDateTime ? formatCellDate(table, row.original.firstSeenDateTime) : '—'),
  },
  {
    header: dateHeader('Last Seen'),
    accessorKey: 'lastSeenDateTime',
    size: 150,
    cell: ({ row, table }) => (row.original.lastSeenDateTime ? formatCellDate(table, row.original.lastSeenDateTime) : '—'),
  },
  {
    header: 'RSSI',
    accessorKey: 'rssi',
    size: 80,
    cell: ({ row }) => {
      const rssi = row.original.rssi;
      const color = rssi == null ? '#9ca3af' : rssi >= -60 ? 'green' : rssi >= -75 ? 'orange' : 'red';
      return <span style={{ color }}>{rssi ?? '—'}</span>;
    },
  },
  {
    id: 'Location Method',
    accessorKey: 'locationMethodDescr',
    header: 'Location Method',
    size: 70,
    cell: ({ row }) => (
      <span style={{ textAlign: 'center' }} title={row.original.locationMethodDescr}>
        {renderLocationMethod(row.original.locationMethod)}
      </span>
    ),
  },
  {
    header: 'Temp (°F)',
    accessorKey: 'temperatureF',
    size: 100,
    cell: ({ row }) => (row.original.temperatureF != null ? `${row.original.temperatureF.toFixed(1)}°F` : '—'),
  },
  {
    header: 'Battery',
    accessorKey: 'batteryPercent',
    size: 80,
    cell: ({ row }) => {
      const val = row.original.batteryPercent ?? 0;
      const color = val > 80 ? 'green' : val < 20 ? 'red' : 'orange';
      return <span style={{ color }}>{val}%</span>;
    },
  },
];
